import { Tableau } from '~commonInfra/utils/TableauLog';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { getDatasourceInfos, getProjectInfos, getWorkbookInfos } from '~src/spacesPlatform/sagas/SpacesSagaHelpers';
import { IUserInfo } from '~src/userManagement/users/models/IUserInfo';

import { PreviewExtractionRequest } from '../serverApi/requests/PreviewExtractionRequest';
import { IPreviewItem, IPreviewResult } from '../serverApi/types/IPreviewResult';
import { ISelectionQuery } from '../serverApi/types/ISelectionQuery';
import { explicitSelectionIds, parseInValues } from '../utils/relaySelection';
import { hasErrors } from '../utils/resultErrors';

/** Content types a plan row can be. Matches relaySelection's supported set. */
export type PlanContentType = 'workbook' | 'datasource' | 'project';

const WIRE_TO_CONTENT_TYPE: { [wire: string]: PlanContentType } = {
  WORKBOOKS: 'workbook',
  WORKBOOK: 'workbook',
  DATASOURCES: 'datasource',
  DATASOURCE: 'datasource',
  PROJECTS: 'project',
  PROJECT: 'project',
};

export const PREVIEW_PAGE_SIZE = 100;

/** One row of the plan contents / extraction preview table. */
export interface IPlanContentRow {
  /** `<contentType>:<id>`; stable across pages. */
  readonly key: string;
  /** Numeric DB id — the value of the plan's `id EQUALS` / `id IN` criterion. */
  readonly id: string;
  /** Content LUID; only preview rows carry it (fallback rows come from the plan's DB ids). */
  readonly luid?: string;
  readonly contentType: PlanContentType;
  readonly name: string;
  readonly owner?: IUserInfo;
  readonly location: string;
  readonly updatedAt?: Date;
  /** The item no longer resolves (deleted, or the viewer can't see it). */
  readonly unavailable: boolean;
  /** Listed by one of the plan's own selection queries (vs. pulled in by the server's resolution). */
  readonly inSelection: boolean;
  /** False when the server resolved the item but won't download it (PreviewItem.downloadable). */
  readonly downloadable: boolean;
}

export interface IPlanContentsPage {
  readonly rows: ReadonlyArray<IPlanContentRow>;
  /** Undefined when there are no more pages. */
  readonly nextPageToken?: string;
  readonly totalCount: number;
  /** True when rows came from the plan's selection queries because previewExtraction is unavailable. */
  readonly isFallback: boolean;
  /**
   * Fallback only: how many selection queries select by filter (anything other than explicit ids), so
   * their items can't be listed without the server's resolution.
   */
  readonly filterQueryCount?: number;
}

export function rowKey(contentType: PlanContentType, id: string): string {
  return contentType + ':' + id;
}

export function toPlanContentType(wire: string): PlanContentType {
  return wire ? WIRE_TO_CONTENT_TYPE[wire.toUpperCase()] : undefined;
}

/** The items an explicit-id selection query lists (see relaySelection.explicitSelectionIds). */
function explicitIdItems(query: ISelectionQuery): { contentType: PlanContentType, ids: string[] } {
  const contentType = query ? toPlanContentType(query.contentType) : undefined;
  const ids = contentType ? explicitSelectionIds(query) : undefined;
  return ids ? { contentType: contentType, ids: ids } : undefined;
}

function explicitItemsOf(queries: ReadonlyArray<ISelectionQuery>): Array<{ contentType: PlanContentType, id: string }> {
  const items: Array<{ contentType: PlanContentType, id: string }> = [];
  const seen: { [key: string]: boolean } = {};
  (queries || []).forEach(function(query) {
    const listed = explicitIdItems(query);
    (listed ? listed.ids : []).forEach(function(id) {
      const key = rowKey(listed.contentType, id);
      if (!seen[key]) {
        seen[key] = true;
        items.push({ contentType: listed.contentType, id: id });
      }
    });
  });
  return items;
}

/** Keys of every item the plan's selection lists explicitly by id. */
export function selectionKeys(queries: ReadonlyArray<ISelectionQuery>): { [key: string]: boolean } {
  const keys: { [key: string]: boolean } = {};
  explicitItemsOf(queries).forEach(function(item) { keys[rowKey(item.contentType, item.id)] = true; });
  return keys;
}

/**
 * Selection queries with the given row keys removed. An `id EQUALS` query for a removed item is
 * dropped; an `id IN` query loses the removed ids and is dropped once empty (the server rejects an
 * empty IN array). Filter queries are kept.
 */
export function withoutItems(queries: ReadonlyArray<ISelectionQuery>, keys: ReadonlyArray<string>): ISelectionQuery[] {
  const result: ISelectionQuery[] = [];
  (queries || []).forEach(function(query) {
    const listed = explicitIdItems(query);
    if (!listed) {
      result.push(query);
      return;
    }
    const isRemoved = function(id: string | number): boolean { return keys.indexOf(rowKey(listed.contentType, String(id))) !== -1; };
    const criterion = query.criteria[0];
    if (criterion.operator === 'EQUALS') {
      if (!isRemoved(criterion.value)) {
        result.push(query);
      }
      return;
    }
    const values = parseInValues(criterion.value);
    const kept = values.filter(function(value) { return !isRemoved(value); });
    if (kept.length === values.length) {
      result.push(query);
    } else if (kept.length) {
      result.push({ ...query, criteria: [{ ...criterion, value: JSON.stringify(kept) }] });
    }
  });
  return result;
}

function idsOf(items: ReadonlyArray<{ contentType: PlanContentType, id: string }>, type: PlanContentType): string[] {
  return items.filter(function(item) { return item.contentType === type; }).map(function(item) { return item.id; });
}

interface IResolvedInfo {
  name: string;
  owner?: IUserInfo;
  location: string;
  updatedAt?: Date;
}

// Location is the containing project (or personal space). Projects only carry a parentId, so a
// project row's location stays blank rather than costing another lookup.
function toResolved(info: any): IResolvedInfo {
  const container = info.location || info.project;
  return {
    name: info.name,
    owner: info.owner,
    location: container && container.name ? container.name : (info.projectName || ''),
    updatedAt: info.updatedAt,
  };
}

/**
 * Looks up owner / location / modified for the given items with the regular list APIs. Ids are the
 * content-core DB ids the plan stores. Workbooks and projects resolve by id directly. Data sources go
 * through `DataSources.fromContent` (via getDatasourceInfos), which maps them to unified data source
 * ids first; the result's `idForFavoriting` is the original data source id, used to join back.
 */
export function resolveItems(
  items: ReadonlyArray<{ contentType: PlanContentType, id: string }>
): Promise<{ [key: string]: IResolvedInfo }> {
  const resolved: { [key: string]: IResolvedInfo } = {};
  const workbookIds = idsOf(items, 'workbook');
  const datasourceIds = idsOf(items, 'datasource');
  const projectIds = idsOf(items, 'project');

  const asContent = function(ids: string[], contentType: PlanContentType): any[] {
    return ids.map(function(id) { return { id: id, contentType: contentType }; });
  };
  const put = function(contentType: PlanContentType, id: string, info: any): void {
    if (id && info) {
      resolved[rowKey(contentType, String(id))] = toResolved(info);
    }
  };

  // A failed lookup leaves that type's rows unavailable rather than failing the whole page (e.g. a data
  // source id that getDataConnectionIdsFromDatasources rejects).
  const lookup = function(ids: string[], contentType: PlanContentType, fetch: (content: any[]) => Promise<any[]>): Promise<any[]> {
    if (!ids.length) {
      return Promise.resolve([]);
    }
    return fetch(asContent(ids, contentType)).catch(function(error: any) {
      Tableau.Logger().info('Content Relay: failed to look up ' + contentType + ' items: ' + error);
      return [];
    });
  };

  return Promise.all([
    lookup(workbookIds, 'workbook', getWorkbookInfos),
    lookup(datasourceIds, 'datasource', getDatasourceInfos),
    lookup(projectIds, 'project', getProjectInfos),
  ]).then(function(results: any[][]) {
    (results[0] || []).forEach(function(info) { put('workbook', info.id, info); });
    (results[1] || []).forEach(function(info) { put('datasource', info.idForFavoriting || info.id, info); });
    (results[2] || []).forEach(function(info) { put('project', info.id, info); });
    return resolved;
  });
}

function buildRows(
  items: ReadonlyArray<{ contentType: PlanContentType, id: string, luid?: string, name?: string, downloadable?: boolean }>,
  resolved: { [key: string]: IResolvedInfo },
  inSelection: { [key: string]: boolean }
): IPlanContentRow[] {
  return items.map(function(item) {
    const key = rowKey(item.contentType, item.id);
    const info = resolved[key];
    return {
      key: key,
      id: item.id,
      luid: item.luid || undefined,
      contentType: item.contentType,
      name: info ? info.name : (item.name || ''),
      owner: info ? info.owner : undefined,
      location: info ? info.location : '',
      updatedAt: info ? info.updatedAt : undefined,
      unavailable: !info,
      inSelection: !!inSelection[key],
      // Only the preview reports downloadability; fallback rows are assumed downloadable.
      downloadable: item.downloadable !== false,
    };
  });
}

function fromPreviewItems(
  previewItems: ReadonlyArray<IPreviewItem>
): Array<{ contentType: PlanContentType, id: string, luid: string, name: string, downloadable: boolean }> {
  const items: Array<{ contentType: PlanContentType, id: string, luid: string, name: string, downloadable: boolean }> = [];
  (previewItems || []).forEach(function(item) {
    const contentType = toPlanContentType(item.contentType);
    // Skip types this UI doesn't model yet (e.g. users / groups) rather than render a blank row.
    if (contentType && item.id) {
      items.push({ contentType: contentType, id: String(item.id), luid: item.luid, name: item.name, downloadable: item.downloadable });
    }
  });
  return items;
}

// Preview rows as the preview reports them, without owner / location / modified. The server resolved them,
// so they're available.
function previewRows(
  items: ReadonlyArray<{ contentType: PlanContentType, id: string, luid: string, name: string, downloadable: boolean }>,
  inSelection: { [key: string]: boolean }
): IPlanContentRow[] {
  return items.map(function(item) {
    const key = rowKey(item.contentType, item.id);
    return {
      key: key,
      id: item.id,
      luid: item.luid || undefined,
      contentType: item.contentType,
      name: item.name || '',
      location: '',
      unavailable: false,
      inSelection: !!inSelection[key],
      downloadable: item.downloadable !== false,
    };
  });
}

export interface ILoadPlanContentsOptions {
  /**
   * Whether to look up owner / location / modified for preview rows (up to 3 list calls per page). The Run
   * dialog only needs the preview's own name, type and downloadability, so it skips them. Default true.
   */
  readonly withDetails?: boolean;
}

/**
 * Loads one page of the plan's extraction preview: the concrete items its selection resolves to
 * (`previewExtraction`), enriched with owner / location / modified unless `options.withDetails` is false.
 * If previewExtraction isn't
 * available (tabhf-content-mobility ContentMobilityService has no previewExtraction yet), falls back to
 * listing the items the plan's selection names by id, in one page, and counts the filter queries it
 * can't list so the page can say so rather than look empty.
 */
export function loadPlanContentsPage(
  planLuid: string,
  selectionQueries: ReadonlyArray<ISelectionQuery>,
  pageToken?: string,
  options?: ILoadPlanContentsOptions
): Promise<IPlanContentsPage> {
  const withDetails = !options || options.withDetails !== false;
  const inSelection = selectionKeys(selectionQueries);

  const fallback = function(): Promise<IPlanContentsPage> {
    const items = explicitItemsOf(selectionQueries);
    const filterQueryCount = (selectionQueries || []).filter(function(query) { return !explicitIdItems(query); }).length;
    return resolveItems(items).then(function(resolved) {
      return { rows: buildRows(items, resolved, inSelection), totalCount: items.length, isFallback: true, filterQueryCount: filterQueryCount };
    });
  };

  return WebHttpClient.sendRequest(new PreviewExtractionRequest({ planLuid: planLuid, pageSize: PREVIEW_PAGE_SIZE, pageToken: pageToken }))
    .then(function(result: IPreviewResult) { return !result || hasErrors(result) ? Promise.reject(result) : result; })
    .then(
      function(result: IPreviewResult) {
        const items = fromPreviewItems(result.items);
        const page = function(rows: IPlanContentRow[]): IPlanContentsPage {
          return {
            rows: rows,
            nextPageToken: result.nextPageToken || undefined,
            totalCount: result.totalCount || items.length,
            isFallback: false,
          };
        };
        if (!withDetails) {
          return page(previewRows(items, inSelection));
        }
        return resolveItems(items).then(function(resolved) { return page(buildRows(items, resolved, inSelection)); });
      },
      function(error: any) {
        // A later page failing is a real error; only the first page may fall back.
        if (pageToken) {
          return Promise.reject(error);
        }
        Tableau.Logger().info('Content Relay: previewExtraction unavailable, listing plan selection instead.');
        return fallback();
      }
    );
}
